import {X} from 'lucide-react';
import * as React from 'react';
import {Button} from 'src/Button';
import {cn} from 'src/lib/utils';

// The pieces both bulk editors share: a property row that is left unchanged
// until activated, the remove-strip chip, and the agree-or-Mixed helpers.

/** Shows the current value (or the input, once the row is activated). */
export const PropertyRow = ({
  label,
  summary,
  active,
  disabledReason,
  loading,
  onToggle,
  children,
}: {
  label: string;
  summary: React.ReactNode;
  active: boolean;
  disabledReason?: string | null;
  loading?: boolean;
  onToggle: (active: boolean) => void;
  children?: React.ReactNode;
}) => (
  <div className="flex flex-row items-start gap-2">
    <label className="text-muted-foreground w-30 shrink-0 pt-1.5 text-sm">
      {label}
    </label>
    <div className="min-w-0 flex-1">
      {active ? (
        children
      ) : (
        <div className="text-muted-foreground flex min-h-9 flex-col justify-center py-1.5 text-sm">
          <span className="truncate">{summary}</span>
          {disabledReason ? (
            <span className="text-xs opacity-80">{disabledReason}</span>
          ) : null}
        </div>
      )}
    </div>
    <Button
      variant={active ? 'ghost' : 'outline'}
      className="mt-0.5 h-8 shrink-0 px-2.5 text-xs"
      disabled={!active && (Boolean(disabledReason) || loading)}
      onClick={() => onToggle(!active)}
      aria-label={active ? `Leave ${label} unchanged` : `Change ${label}`}
      title={active ? 'Leave unchanged' : undefined}
    >
      {active ? <X className="size-4" /> : 'Change'}
    </Button>
  </div>
);

/** A toggleable chip for the remove strip: click to mark for removal. */
export const RemovalChip = ({
  label,
  count,
  armed,
  onToggle,
}: {
  label: string;
  count: number;
  armed: boolean;
  onToggle: () => void;
}) => (
  <button
    type="button"
    onClick={onToggle}
    className={cn(
      'flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs',
      armed
        ? 'border-destructive text-destructive line-through'
        : 'border-border text-foreground hover:bg-muted/50',
    )}
  >
    <span>{label}</span>
    {count > 1 && <span className="text-muted-foreground">{count}</span>}
  </button>
);

export const uniform = <T, V>(items: T[], pick: (item: T) => V): V | null => {
  if (items.length === 0) return null;
  const first = pick(items[0]);
  return items.every((item) => pick(item) === first) ? first : null;
};

export const MIXED = <span className="italic">Mixed</span>;
